import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Create as CreateIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import SignaturePad from './SignatureCanvas';

type SignatureType = 'draw' | 'type';

interface EnhancedSignaturePadProps {
  onSignatureChange: (isEmpty: boolean) => void;
  onSignatureData: (dataUrl: string) => void;
  onTypedNameChange?: (name: string) => void;
  onSignatureTypeChange?: (type: 'draw' | 'type') => void;
  disabled?: boolean;
  initialData?: string;
  signatureType?: 'draw' | 'type';
  typedName?: string;
  /** When true (e.g. on iPad), only "Type Name" is shown; draw signature is disabled */
  drawSignatureDisabled?: boolean;
}

const EnhancedSignaturePad: React.FC<EnhancedSignaturePadProps> = ({
  onSignatureChange,
  onSignatureData,
  onTypedNameChange,
  onSignatureTypeChange,
  disabled = false,
  initialData,
  signatureType: externalSignatureType,
  typedName: externalTypedName,
  drawSignatureDisabled = false,
}) => {
  const [signatureType, setSignatureType] = useState<SignatureType>(
    drawSignatureDisabled ? 'type' : (externalSignatureType || 'draw')
  );
  const [typedName, setTypedName] = useState<string>(externalTypedName || '');
  const [signatureData, setSignatureData] = useState<string>(initialData || '');
  
  // When draw is disabled (e.g. iPad), force type mode
  useEffect(() => {
    if (drawSignatureDisabled) {
      setSignatureType('type');
      if (onSignatureTypeChange) {
        onSignatureTypeChange('type');
      }
    }
  }, [drawSignatureDisabled, onSignatureTypeChange]);

  // Initialize signature data if provided
  useEffect(() => {
    if (initialData && initialData.trim() !== '') {
      setSignatureData(initialData);
      // If it's an image, we can use it in draw mode
      // If it was a typed name converted to image, user can still choose either method
    }
  }, [initialData]);

  // Convert typed name to image data URL
  const convertNameToImage = (name: string): string => {
    if (!name.trim()) return '';
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Set canvas size
    canvas.width = 600;
    canvas.height = 200;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set font styling - elegant signature style
    ctx.font = 'italic 48px "Brush Script MT", "Lucida Handwriting", cursive, serif';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Draw the name
    ctx.fillText(name.trim(), canvas.width / 2, canvas.height / 2);

    // Convert to data URL
    return canvas.toDataURL('image/png');
  };

  // Handle signature type change
  const handleSignatureTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newType: SignatureType | null
  ) => {
    if (newType !== null && !disabled) {
      // Check if current tab has data - block switching if it does
      const hasDrawData = signatureType === 'draw' && signatureData && signatureData.trim() !== '';
      const hasTypeData = signatureType === 'type' && typedName && typedName.trim() !== '';
      
      if (hasDrawData || hasTypeData) {
        // Block switching - user must clear first
        return;
      }
      
      setSignatureType(newType);
      if (onSignatureTypeChange) {
        onSignatureTypeChange(newType);
      }
      // Clear current data when switching types
      if (newType === 'draw') {
        setTypedName('');
        if (onTypedNameChange) {
          onTypedNameChange('');
        }
      } else {
        setSignatureData('');
      }
      onSignatureChange(true);
      onSignatureData('');
    }
  };

  // Handle typed name change
  useEffect(() => {
    if (signatureType === 'type') {
      const trimmedName = typedName.trim();
      const isEmpty = !trimmedName;
      onSignatureChange(isEmpty);
      
      if (onTypedNameChange) {
        onTypedNameChange(trimmedName);
      }
      
      if (!isEmpty) {
        const dataUrl = convertNameToImage(trimmedName);
        setSignatureData(dataUrl);
        onSignatureData(dataUrl);
      } else {
        setSignatureData('');
        onSignatureData('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedName, signatureType]);

  // Handle drawn signature change
  const handleDrawSignatureChange = (isEmpty: boolean) => {
    if (signatureType === 'draw') {
      onSignatureChange(isEmpty);
    }
  };

  const handleDrawSignatureData = (dataUrl: string) => {
    if (signatureType === 'draw') {
      setSignatureData(dataUrl);
      onSignatureData(dataUrl);
    }
  };

  // Handle clear
  const handleClear = () => {
    if (signatureType === 'draw') {
      // Clear signature data
      setSignatureData('');
      onSignatureChange(true);
      onSignatureData('');
    } else {
      setTypedName('');
      if (onTypedNameChange) {
        onTypedNameChange('');
      }
      setSignatureData('');
      onSignatureChange(true);
      onSignatureData('');
    }
  };

  // Check if current signature is empty
  const isEmpty = signatureType === 'draw' 
    ? !signatureData 
    : !typedName.trim();
  
  // Check if we can switch tabs (only if current tab is empty)
  const canSwitchTabs = isEmpty;

  return (
    <Box>
      {/* Signature Type Selector - hidden on iPad (draw disabled) */}
      {!drawSignatureDisabled && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
            Choose your preferred signing method:
          </Typography>
          <ToggleButtonGroup
            value={signatureType}
            exclusive
            onChange={handleSignatureTypeChange}
            aria-label="signature type"
            fullWidth
            disabled={disabled}
            sx={{
              '& .MuiToggleButton-root': {
                py: 1.5,
                px: 3,
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 500,
                border: '2px solid',
                borderColor: 'divider',
                position: 'relative',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderColor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    borderColor: 'primary.dark',
                  },
                },
                '&:not(.Mui-selected)': {
                  '&:hover': {
                    bgcolor: canSwitchTabs ? 'action.hover' : 'transparent',
                    borderColor: canSwitchTabs ? 'primary.main' : 'divider',
                    cursor: canSwitchTabs ? 'pointer' : 'not-allowed',
                  },
                  opacity: canSwitchTabs ? 1 : 0.5,
                  cursor: canSwitchTabs ? 'pointer' : 'not-allowed',
                },
              },
            }}
          >
            <ToggleButton value="draw" aria-label="draw signature">
              <CreateIcon sx={{ mr: 1, fontSize: 20 }} />
              Draw Signature
            </ToggleButton>
            <ToggleButton value="type" aria-label="type name">
              <EditIcon sx={{ mr: 1, fontSize: 20 }} />
              Type Name
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {drawSignatureDisabled && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
          Enter your name below to sign.
        </Typography>
      )}

      {/* Subtle hint when switching is blocked */}
      {!drawSignatureDisabled && !canSwitchTabs && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.8125rem',
              fontStyle: 'italic',
            }}
          >
            Clear {signatureType === 'draw' ? 'signature' : 'name'} to switch
          </Typography>
        </Box>
      )}

      {/* Signature Input Area */}
      {signatureType === 'draw' ? (
        <SignaturePad
          key="draw-signature"
          initialData={signatureType === 'draw' ? signatureData : ''}
          onSignatureChange={handleDrawSignatureChange}
          onSignatureData={handleDrawSignatureData}
          disabled={disabled}
        />
      ) : (
        <Box>
          <Paper
            elevation={0}
            sx={{
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              p: 3,
              mb: 2,
              minHeight: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {typedName.trim() ? (
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive, serif',
                  fontStyle: 'italic',
                  color: 'text.primary',
                  fontWeight: 400,
                }}
              >
                {typedName}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  opacity: 0.5,
                  fontStyle: 'italic',
                }}
              >
                Your typed name will appear here
              </Typography>
            )}
          </Paper>
          <TextField
            fullWidth
            label="Enter your full name"
            placeholder="Type your name as you would sign it"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            disabled={disabled}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                fontSize: '1rem',
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
            helperText="This will be used as your signature"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={disabled || isEmpty}
            sx={{ mb: 1 }}
          >
            Clear Name
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EnhancedSignaturePad;

